import React, { useState } from "react";
import { Table, Button } from "react-bootstrap";
import PointsBreakdown from "./PointsBreakdown";
import { Leaderboard, LeaderboardTeam } from "../../../types/model";
import { useAppSelector } from "../../../store/hooks";
import { Root } from "react-dom/client";
import { RootState } from "../../../store/store";
import { FaFlagCheckered } from "react-icons/fa";
import { IoGlobeSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

interface LeaderboardTableProps {
	leaderboard: Leaderboard;
	showPointsBreakdown: boolean;
	showLeagueName: boolean;
	expanded: boolean;
}

const LeaderBoardTable: React.FC<LeaderboardTableProps> = ({
	leaderboard,
	showPointsBreakdown,
	showLeagueName = false,
	expanded,
}) => {
	const navigate = useNavigate();
	const { selectedLeague } = useAppSelector(
		(state: RootState) => state.fantasy,
	);
	const [team, setTeam] = useState<LeaderboardTeam>({} as LeaderboardTeam);
	const [showModal, setShowModal] = useState(false);

	const handleLeagueClick = () => {
		if (selectedLeague) {
			navigate(`/league/${selectedLeague._id}`);
		}
	};

	const displayedTeams = expanded
		? leaderboard.teams
		: (leaderboard.teams ?? []).slice(0, 5);

	return (
		<div
			className="bg-secondary text-light px-3 pb-3 pt-2 rounded"
			style={{
				maxHeight: "20rem",
				overflow: "auto",
				// scrollbarWidth: "thin", /* Firefox */
				scrollbarColor: "#6c757d #343a40" /* Firefox */,
				msOverflowStyle: "none" /* IE and Edge */,
			}}
		>
			<style>
				{`
				/* Chrome, Safari, Edge scrollbar styles */
				div::-webkit-scrollbar {
					width: 8px;
				}
				div::-webkit-scrollbar-track {
					background: #343a40;
					border-radius: 4px;
				}
				div::-webkit-scrollbar-thumb {
					background-color: #6c757d;
					border-radius: 4px;
				}
				`}
			</style>{" "}
			{showLeagueName && (
				<Button
					variant="outline-info"
					className=" mb-2 text-start border-0 hover-text hover py-0 px-2"
					onClick={handleLeagueClick}
					style={{
						cursor: "pointer",
					}}
				>
					{selectedLeague.featured ? (
						<IoGlobeSharp className="me-1 mb-1" size={20} />
					) : (
						<FaFlagCheckered className="me-1 mb-1" size={20} />
					)}
					{selectedLeague.name.toLocaleUpperCase()}
				</Button>
			)}
			<Table
				hover
				striped
				size="sm"
				className="bg-secondary text-light border-secondary rounded-3"
			>
				<thead>
					<tr>
						<th className="border-info text-info bg-secondary text-center">
							#
						</th>
						<th className="border-info text-info bg-secondary">USERNAME</th>
						<th className="border-info text-info bg-secondary text-end">
							POINTS
						</th>
						{showPointsBreakdown && (
							<th className="border-info text-info bg-secondary"></th>
						)}
					</tr>
				</thead>
				<tbody>
					{Array.isArray(displayedTeams) &&
						displayedTeams.map((team, index) => (
							<tr
								key={index}
								style={{ height: "2.5rem", verticalAlign: "middle" }}
							>
								<td className="border-info text-light bg-secondary px-3 align-middle text-center">
									{team.rank === 1 ? (
										<span
											role="img"
											aria-label="First place"
											style={{ color: "gold" }}
										>
											🏆
										</span>
									) : team.rank === 2 ? (
										<span
											role="img"
											aria-label="Second place"
											style={{ color: "silver" }}
										>
											🥈
										</span>
									) : team.rank === 3 ? (
										<span
											role="img"
											aria-label="Third place"
											style={{ color: "#cd7f32" }}
										>
											🥉
										</span>
									) : (
										team.rank ?? index + 1
									)}
								</td>
								<td className="border-info text-light bg-secondary align-middle">
									{(team.rank ?? 0) < 4 ? (
										<strong>{team.userName.toLocaleUpperCase()}</strong>
									) : (
										team.userName
									)}
								</td>
								<td className="border-info text-light bg-secondary align-middle text-end pe-3">
									{team.totalPoints}
								</td>
								{showPointsBreakdown && (
									<td className="border-info text-light bg-secondary align-middle text-end">
										<Button
											variant="outline-light"
											className="fw-bold "
											size="sm"
											onClick={() => {
												setTeam(team);
												setShowModal(true);
											}}
										>
											BREAKDOWN
										</Button>
									</td>
								)}
							</tr>
						))}
				</tbody>
			</Table>
			{showPointsBreakdown && (
				<PointsBreakdown
					leaderboardTeam={team}
					showModal={showModal}
					handleClose={() => setShowModal(false)}
				/>
			)}
		</div>
	);
};

export default LeaderBoardTable;
